/**
 * Actions
 *
 * ActionCreators are like the "pub" of pubsub. It's just broadcasting an event (action).
 * So, don't couple it with a specific reducer. Let ANY reducer listen to
 * an action.
 *
 * `typesafe-actions` has a nice utility function so we don't need to define action types as
 * constants. Use it together with `getType(type)` in the reducers.
 */
import { createAction } from "typesafe-actions";
import { AccountProfile } from "./reducers";
import { LoadingFunc } from "src/ipm-shared/Utils/ReduxSagaEffects";

type cbType = (err?: any, data?: any) => void;

export type AccountType = "business" | "personal";

export const fetchAccountProfiles = createAction(
  "accountProfile/FETCH",
  resolve => (cb?: cbType, isSkipFetching?: boolean) =>
    resolve({ cb, isSkipFetching })
);

export const setAccountType = createAction(
  "accountProfile/SET_ACCOUNT_TYPE",
  resolve => (type: AccountType) => resolve({ accountType: type })
);

export const setAccountProfiles = createAction(
  "accountProfile/SET",
  resolve => (args: {
    isFetching: boolean;
    hasFetched: boolean;
    profiles: AccountProfile[];
  }) => resolve(args)
);

export const updateAccountProfile = createAction(
  "accountProfile/UPDATE",
  resolve => (form: string, cb?: cbType, loadingFunc?: LoadingFunc) =>
    resolve({ form, cb, loadingFunc })
);

export const setCurrentAccountProfile = createAction(
  "accountProfile/SET_CURRENT",
  resolve => (args: { email: string }) => resolve(args)
);

export const changeUserSetting = createAction(
  "accountProfile/CHANGE_USER_SETTING",
  resolve => (args: {
    enable?: boolean;
    property: string;
    targetUrl?: string;
    refreshDisable?: boolean;
    modalID?: string;
    value?: string;
    disableGlobalLoader?: boolean;
    fetchAccountProfile?: boolean;
  }) => resolve(args)
);

export const changeTheme = createAction(
  "accountProfile/CHANGE_THEME",
  resolve => (args: { targetUrl?: string; themeName: string }) => resolve(args)
);

export const setNricPhotos = createAction(
  "accountProfile/SET_NRIC_PHOTOS",
  resolve => (extraInfo: any) => resolve({ extraInfo })
);

export const fetchNricPhotos = createAction(
  "accountProfile/FETCH_NRIC_PHOTOS",
  resolve => (cb?: cbType, id?: number) => resolve({ cb, id })
);

export const updateUserSetting = createAction(
  "accountProfile/UPDATE_USER_SETTING",
  resolve => (
    prop: keyof AccountProfile["userSettingRequired"] | "",
    value: any
  ) => resolve({ prop, value })
);

// @Deprecated
// export const setCrossBorderCardFlag = createAction(
//   "accountProfile/SET_CROSS_BORDER_CARD_FLAG",
//   resolve => (crossBorderCardFlag: boolean) => resolve({ crossBorderCardFlag })
// );

// This will be called when changing country dropdown in dashboard
// Country ID is the unique property in access token data that is allowed to change.
// It stays under account profile
export const setCurrentCountry = createAction(
  "accountProfile/SET_CURRENT_COUNTRY",
  resolve => (args: { code: string; id: number }) => resolve(args)
);

export const setCurrentCurrency = createAction(
  "accountProfile/SET_CURRENT_CURRENCY_ID",
  resolve => (args: { code: string; id: number }) => resolve(args)
);

export const enterProgram = createAction(
  "accountProfile/ENTER_PROGRAM",
  resolve => (programName: string) => resolve({ programName })
);

export const joinReferral = createAction(
  "accountProfile/JOIN_REFERRAL",
  resolve => (cb?: cbType) => resolve({ cb })
);

// TODO: this could be in another state, not account profile
export const getReferralReport = createAction(
  "accountProfile/GET_REFERRAL_REPORT",
  resolve => (cb?: cbType) => resolve({ cb })
);

// TODO: this could be in another state, not account profile
export const loginMyInfoSingPass = createAction(
  "accountProfile/LOGIN_MY_INFO_SING_PASS",
  resolve => (isModal: boolean, redirectUrl: string, isBiz?: boolean) =>
    resolve({ isModal, redirectUrl, isBiz })
);

// TODO: this could be in another state, not account profile
export const fetchMyInfoSingPass = createAction(
  "accountProfile/FETCH_MY_INFO_SING_PASS",
  resolve => (cb?: cbType, isBiz?: boolean) => resolve({ cb, isBiz })
);

// TODO: this could be in another state, not account profile
export const confirmMyInfoSingPass = createAction(
  "accountProfile/CONFIRM_MY_INFO_SING_PASS",
  resolve => (
    cb?: cbType,
    loadingFunc?: LoadingFunc,
    isBiz?: boolean,
    isRemove?: boolean
  ) => resolve({ cb, loadingFunc, isBiz, isRemove })
);

export const closeAccount = createAction(
  "accountProfile/CLOSE_ACCOUNT",
  resolve => (data: any, cb?: cbType) => resolve({ data, cb })
);

export const updateAdditionalData = createAction(
  "accountProfile/UPDATE_ADDITIONAL_DATA",
  resolve => (form: string, cb?: cbType, loadingFunc?: LoadingFunc) =>
    resolve({ form, cb, loadingFunc })
);
